import { useState, useRef, useMemo } from 'react'
import { Loader2, X } from 'lucide-react'
import { useAllSystemIds } from '@/hooks/use-systems'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 50

interface SystemIdComboboxProps {
  value: string
  onChange: (value: string) => void
  onSubmit?: (value: string) => void
  placeholder?: string
}

export default function SystemIdCombobox({
  value,
  onChange,
  onSubmit,
  placeholder = 'Enter system ID…',
}: SystemIdComboboxProps) {
  const [fetchEnabled, setFetchEnabled] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedIdx, setSelectedIdx] = useState(-1)
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const inputRef = useRef<HTMLInputElement>(null)

  const { systemIds, isLoading, hasAnyData } = useAllSystemIds(fetchEnabled)

  const allFilteredIds = useMemo(() => {
    if (!value) return systemIds
    const q = value.toLowerCase()
    return systemIds.filter((id) => id.toLowerCase().includes(q))
  }, [systemIds, value])

  const visibleIds = allFilteredIds.slice(0, visibleCount)
  const hasMore = visibleCount < allFilteredIds.length

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      setVisibleCount((n) => n + PAGE_SIZE)
    }
  }

  const showDropdown = open

  const handleFocus = () => {
    setFetchEnabled(true)
    setOpen(true)
  }

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 150)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    setSelectedIdx(-1)
    setVisibleCount(PAGE_SIZE)
    setOpen(true)
  }

  const selectItem = (id: string) => {
    onChange(id)
    setOpen(false)
    onSubmit?.(id)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIdx((i) => Math.min(i + 1, visibleIds.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIdx((i) => Math.max(i - 1, -1))
    } else if (e.key === 'Enter' && selectedIdx >= 0) {
      e.preventDefault()
      selectItem(visibleIds[selectedIdx])
    } else if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn(
          'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 font-mono text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          value && 'pr-8'
        )}
      />
      {value && (
        <button
          type="button"
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          onMouseDown={(e) => { e.preventDefault(); onChange(''); setVisibleCount(PAGE_SIZE); setOpen(false) }}
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {showDropdown && (!hasAnyData || visibleIds.length > 0 || isLoading) && (
        <div
          className="absolute left-0 top-full mt-1 w-full min-w-[20rem] rounded-md border border-border bg-popover shadow-lg z-50 overflow-y-auto max-h-60"
          onScroll={handleDropdownScroll}
        >
          {!hasAnyData && isLoading ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading systems…
            </div>
          ) : (
            <>
              <ul>
                {visibleIds.map((id, i) => (
                  <li key={id}>
                    <button
                      type="button"
                      onMouseDown={() => selectItem(id)}
                      className={cn(
                        'w-full px-3 py-2 text-left text-sm font-mono transition-colors truncate',
                        i === selectedIdx
                          ? 'bg-accent text-accent-foreground'
                          : 'hover:bg-accent/50'
                      )}
                    >
                      {id}
                    </button>
                  </li>
                ))}
              </ul>
              {(isLoading || hasMore) && (
                <div className="flex items-center gap-2 px-4 py-2 border-t border-border text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {isLoading ? 'Loading more pools…' : 'Scroll for more…'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
